import classnames from 'classnames';
import ScholarshipBadge from 'src/components/CollegeSearch/ScholarshipBadge';
import React from 'react';
import {
  formatCollegeNameForCard,
  showTermNotOfferedModal,
} from 'shared/components/college-card/utils';
import FollowButton from 'shared/components/follow-button';
import {earning} from 'shared/utils';
import 'stylesheets/components/college-card-transfers.scss';
import ContextualDataText from './contextual-data-text';
import DeadlineText from './deadline-text';
import SortingInfo, {SortingInfoProps} from './sorting-info';
import StatusText from './status-text';
import {Box} from '@mui/material';
import styles from './college-card.module.scss';
import InfoTooltip from '../info-tooltip';
import {useSelector} from 'react-redux';
import {successfulLoginRedirectPath} from '../../../src/utils/storage';
import {SessionStorage} from '../../../src/utils/session-storage';
import browserHistory from '../../history';
import {Store} from 'src/types/store';

interface CollegeEarningRelationship {
  statusType?: string;
  deadlineDay?: string | number | null;
  deadlineMonth?: string | number | null;
  deadlineYear?: string | number | null;
  hasEarnings?: boolean;
  showSubmissionTerm?: boolean;
  minGPA?: number;
  replacementAward?: {displayData: {title: string; subtitle: string}};
  spotAward?: {name: string};
  termDeferred?: boolean;
  churnedAt?: string;
  amount?: number;
}

interface CollegeData {
  _id: string;
  name: string;
  city?: string;
  state: string;
  logoUrl: string;
  slug: string;
  bannerUrl?: string;
  following?: boolean;
  isFollowing?: boolean;
  deadlineMonth?: string | number | null;
  deadlineDay?: string | number | null;
  deadlineYear?: string | number | null;
  amount?: string | number;
  earningRelationship?: CollegeEarningRelationship;
  isPartner?: boolean;
  category?: string;
  avgNetPrice?: string | number;
  tuition?: string | number;
  graduationRate?: string | number;
  forbesRank?: string | number;
  acceptanceRate?: string | number;
  forbesRankYear?: string | number;
  usNewsRank?: string | number;
  usNewsRankYear?: string | number;
  usNewsListType?: string;
  totalUndergraduates?: string | number;
  amountLoading?: boolean;
  studentSubmissionTerm?: string;
  earningsPossible?: boolean;
}

interface Props {
  college: CollegeData;
  followCollege: (id: string) => void;
  unfollowCollege: (id: string) => void;
  showDeadline?: boolean;
  showDeadlineYear?: boolean;
  showStatus?: boolean;
  showFollow?: boolean;
  showBadge?: boolean;
  bucketID?: string;
  sortBy?: string;
  condensed?: boolean;
  showSortInfo?: boolean;
  shortCard?: boolean;
  simplified?: boolean;
  communityCollegeInfo?: {
    transferSemester: string;
  };
  openTermNotOfferedModal?: (id: string) => void;
  wideCard?: boolean;
  userType?: string;
}

const CollegeCard = ({
  college,
  followCollege,
  unfollowCollege,
  showDeadline,
  showDeadlineYear,
  showStatus,
  showFollow = true,
  showBadge = true,
  bucketID,
  sortBy,
  condensed,
  shortCard = false,
  simplified,
  communityCollegeInfo,
  showSortInfo,
  openTermNotOfferedModal,
  wideCard,
  userType,
}: Props) => {
  const authenticated = useSelector((state: Store) => state.authenticated);

  const collegeName = () =>
    formatCollegeNameForCard(college.name, showDeadline, college.earningRelationship);

  const isFollowing = !!college.isFollowing;

  const handleFollowClick = (e: React.MouseEvent) => {
    e.preventDefault();

    if (isFollowing) {
      unfollowCollege(college._id);
    } else {
      if (userType === 'public') {
        sessionStorage.setItem(SessionStorage.FOLLOW_COLLEGE_ID, college._id);
        successfulLoginRedirectPath.set(`edu/${college.slug}`);
        browserHistory.push('/colleges/search/sign-up-first', {from: window.location.pathname});
      } else {
        followCollege(college._id);
      }

      if (openTermNotOfferedModal && showTermNotOfferedModal(college, communityCollegeInfo)) {
        openTermNotOfferedModal(college._id);
      }
    }
  };

  const deadline = showDeadline && college.deadlineMonth !== null && college.deadlineDay !== null;
  const earningsPossible = college.earningRelationship?.statusType
    ? earning(college.earningRelationship.statusType)
    : college.earningsPossible;
  const containerClassName = classnames('college-card-transfers', {
    condensed,
    'short-card': shortCard,
    [styles.wideCollegeCard]: wideCard,
    simplified,
  });

  const loginText =
    'Create an account or log in to view potential earnings from this college partner.';

  return (
    <div className={containerClassName}>
      {college.bannerUrl ? (
        <img
          src={college.bannerUrl}
          className="college-card-transfers-banner"
          style={{backgroundImage: college.bannerUrl}}
          alt={`${college.name} - Banner Image`}
          aria-hidden="true"
        />
      ) : (
        <div className="college-card-transfers-banner" />
      )}
      <img
        src={college.logoUrl}
        className="college-card-transfers-logo"
        alt=""
        aria-hidden="true"
      />
      <div
        className={classnames('college-card-transfers-content', {
          [styles.bottomContainer]: wideCard,
        })}
      >
        <Box flex="1 1 0">
          {showSortInfo && (
            <SortingInfo
              college={college as unknown as SortingInfoProps['college']}
              sortBy={sortBy}
            />
          )}
          <ContextualDataText college={college} bucketID={bucketID} />
          <div className="college-card-transfers-header-container">
            <div
              className={classnames('college-card-transfers-header', {
                'with-deadline': deadline,
              })}
            >
              <p
                className={classnames('college-card-transfers-name', {
                  condensed,
                })}
              >
                {collegeName()} {showBadge && earningsPossible && <ScholarshipBadge />}
              </p>
              <p className="college-card-transfers-location">
                {college.city}, {college.state}
                {!authenticated && college.isPartner && (
                  <>
                    <InfoTooltip className="college-card-signed-out-info-mobile">
                      {loginText}
                    </InfoTooltip>
                    <span className="college-card-signed-out-info">{loginText}</span>
                  </>
                )}
              </p>
            </div>
            {deadline && (
              <DeadlineText
                deadlineMonth={college.deadlineMonth}
                deadlineDay={college.deadlineDay}
                deadlineYear={college.deadlineYear}
                earningRelationship={college.earningRelationship}
                showDeadlineYear={showDeadlineYear}
                simplified={simplified}
              />
            )}
          </div>
        </Box>
        {wideCard && showFollow && (
          <Box pb={2} flex="1 1 0" alignSelf="flex-end">
            <FollowButton
              collegeName={college.name}
              following={!!isFollowing}
              outline
              onClick={handleFollowClick}
            />
          </Box>
        )}
      </div>
      {!wideCard && (
        <div className="college-card-bottom-container">
          {showStatus && (
            <StatusText
              following={isFollowing}
              amount={college.amount}
              earningRelationship={college.earningRelationship}
              condensed={condensed}
              amountLoading={college.amountLoading}
              simplified={simplified}
            />
          )}
          <div
            className={classnames('college-card-transfers-follow', {
              condensed,
            })}
          >
            {showFollow && (
              <FollowButton
                collegeName={college.name}
                following={!!isFollowing}
                outline
                onClick={handleFollowClick}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default CollegeCard;
